import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, TextInput, View, Text, StyleSheet, ScrollView } from 'react-native';
import type { StorageInterface } from '../../../stores/childNavStores';
import { AccountPracInfoStorage } from '../../../storage/AccountPracInfoStorage';
import { OrderedDualListSelector, type OrderedOption } from './OrderedDualListSelector';
import { AdminSegmentStorage } from '../../../services/AdminSegmentStorage';
import { AdminAssessmentStorage } from '../../../services/AdminAssessmentStorage';
import { createcontentService } from '../../../services/ContentService';
import { useContentStore } from '../../../contexts/StoreContext';
import type { AdminSegmentDto } from '../../../types/adminSegment';
import type { AdminAssessmentDto } from '../../../types/adminAssessment';
import type {
  AccountPlaDto,
  AccountPracInfoResponseDto,
  AccountSettingsDto,
  AccountSettingsUpdateDto,
  PracAccountSettingUpdateDto,
  AdminUVarDto,
  AdminHtmlAnswerDto,
  AdminPreLoadDto,
  AdminAssessmentReportDto,
  SkinPreloadOverrideDto,
} from '../../../types/api';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import { resolveAuthContext } from '../../../services/auth/authContext';

interface AssessmentOptionsViewProps {
  accountData?: AccountPracInfoResponseDto | null;
  isLoading?: boolean;
  error?: string | null;
  storage?: StorageInterface | null;
  accountId?: string | null;
  apiService?: any;
}

const ASSESSMENT_OPTIONS_SPACING = {
  topPanelPadding: 14,
  topActionsBottom: 6,
  saveButtonTop: 2,
  sectionSpacerRow: 12,
} as const;

const parsePos = (value: string): number => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

// The "Close" AdminSegment — always part of an assessment's measures, never something an
// account can opt out of, so it's hidden from the Measures checkbox list entirely (rather than
// shown pre-checked-and-disabled) and force-included wherever the enabled-segments set is read
// or saved, regardless of what a PLA's stored surveyblist preload actually contains.
const CLOSE_ADMIN_SEGMENT_ID = '11134';

// pla.Reports (AccountPlaDto.Reports, account-settings API model) round-trips through the
// backend as raw, unnormalized JSON — unlike assessment.reports (content/template model),
// which AdminAssessmentStorage.normalizeAssessment already converts to camelCase. So a saved
// AdminAssessmentReportDto here can come back with PascalCase fields (ReportId/DName/
// TargetType) even though the TS type declares camelCase, which otherwise silently reads as
// undefined on reload (e.g. every checkbox showing unchecked even right after a successful
// save). Read defensively for both casings wherever pla.Reports entries are consumed.
const getReportId = (report: any): string => report?.reportId ?? report?.ReportId ?? '';

const getAccountSettingsSignature = (data?: AccountPracInfoResponseDto | null): string => {
  if (!data?.AccountSettings) {
    return 'none';
  }
  return JSON.stringify({
    UVars: data.AccountSettings.UVars,
    PlaList: data.AccountSettings.PlaList,
  });
};

const resolveUserStore = (): any => resolveAuthContext().userStore || null;

const resolveApiService = (apiService: any): any => {
  return apiService?.processServiceRequestWithRefresh ? apiService : null;
};

export const AssessmentOptionsView: React.FC<AssessmentOptionsViewProps> = ({
  accountData,
  isLoading = false,
  error = null,
  storage = null,
  accountId = null,
  apiService = null,
}) => {
  const uVars = accountData?.AccountSettings?.UVars ?? [];
  const plaList = accountData?.AccountSettings?.PlaList ?? [];
  const skinUVars = accountData?.AccountSettings?.SkinDef?.SkinUVars ?? [];
  const skinAssessmentList = accountData?.AccountSettings?.SkinDef?.AssessmentList ?? [];
  const languageTypeId = accountData?.AccountSettings?.SkinDef?.LanguageTypeId || '1';
  // Account-level "Return Website" (Account > Settings) — the default redirect target a PLA's
  // own ReturnUrl falls back to when left blank. Shown as the Return Website field's placeholder
  // below; the backend applies the same fallback when building SecureLoginResponseDto.completeUrl.
  const accountReturnWebsite = accountData?.AccountSettings?.PracInfo?.Website ?? '';
  
  const contentStore = useContentStore();
  const adminSegmentStorage = useMemo(() => storage ? new AdminSegmentStorage(storage) : null, [storage]);
  const adminAssessmentStorage = useMemo(() => storage ? new AdminAssessmentStorage(storage) : null, [storage]);
  const contentService = useMemo(() => {
    const resolvedApiService = apiService || (globalThis as any).__proGroupsApiService || (globalThis as any).__childNavApiService;
    return resolvedApiService ? createcontentService(resolvedApiService, contentStore.getState()) : null;
  }, [apiService, contentStore]);
  
  const sortedPlaList = useMemo(() => {
    return [...plaList].sort((a, b) => parsePos(a.Pos) - parsePos(b.Pos));
  }, [plaList]);

  const uVarOptions = useMemo(() => {
    return skinUVars.map((item) => ({
      id: item.MvarName,
      label: item.DName || item.MvarName,
    }));
  }, [skinUVars]);

  const initialSelectedUVarIds = useMemo(() => {
    return uVars.map((uVar) => uVar.MvarName);
  }, [uVars]);

  const initialUVarHtmlAnsState = useMemo(() => {
    const state: Record<string, AdminHtmlAnswerDto[]> = {};
    uVars.forEach((uVar) => {
      state[uVar.MvarName] = uVar.HtmlUvarAns ?? [];
    });
    return state;
  }, [uVars]);

  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isSavingRemote, setIsSavingRemote] = useState(false);
  const [hydratedAccountId, setHydratedAccountId] = useState<string | null>(null);
  const [hydratedSettingsSignature, setHydratedSettingsSignature] = useState<string>('none');
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isEditingUVars, setIsEditingUVars] = useState(false);
  const [editingUVarMvarName, setEditingUVarMvarName] = useState<string | null>(null);
  const hasPendingUserEditsRef = useRef(false);
  const editingUVarsRef = useRef(false);
  
  const currentSettingsSignature = useMemo(() => getAccountSettingsSignature(accountData), [accountData]);
  const resolvedApiService = useMemo(() => resolveApiService(apiService), [apiService]);
  const hasLoadedAccountSettings = !!accountData?.AccountSettings;
  const localStateMatchesCurrentSettings = hydratedSettingsSignature === currentSettingsSignature;

  // State for UVars editing
  const [selectedUVarIds, setSelectedUVarIds] = useState<string[]>(initialSelectedUVarIds);
  const [uVarHtmlAnsState, setUVarHtmlAnsState] = useState<Record<string, AdminHtmlAnswerDto[]>>(initialUVarHtmlAnsState);

  // State for PLA Preloads and Reports
  const [plaPreloadsState, setPlaPreloadsState] = useState<Record<string, AdminPreLoadDto[]>>({});
  const [plaReportsState, setPlaReportsState] = useState<Record<string, AdminAssessmentReportDto[]>>({});
  
  // State for PLA assessment segment and report enables
  const [plaEnabledSegments, setPlaEnabledSegments] = useState<Record<string, Set<string>>>({});
  const [plaEnabledReports, setPlaEnabledReports] = useState<Record<string, Set<string>>>({});
  
  // State for AdminSegment cache (surveybId -> AdminSegmentDto)
  const [segmentCache, setSegmentCache] = useState<Record<string, AdminSegmentDto>>({});
  // State for AdminAssessment cache (sourceId/assessmentId -> AdminAssessmentDto)
  const [cachedAssessments, setCachedAssessments] = useState<Record<string, AdminAssessmentDto>>({});
  // Preload field edit values per PLA: key=plaKey, value={fieldCode -> stringValue}
  const [plaPreloadEditsState, setPlaPreloadEditsState] = useState<Record<string, Record<string, string>>>({});
  // Per-PLA "Return Website" edit value (AccountPlaDto.ReturnUrl): key=plaKey -> string.
  // Empty string is a valid saved value — the backend then falls back to the account Return Website.
  const [plaReturnUrlState, setPlaReturnUrlState] = useState<Record<string, string>>({});
  // Dirty flag for PLA section edits
  const [isPlaEditDirty, setIsPlaEditDirty] = useState(false);

  useEffect(() => {
    if (!hasLoadedAccountSettings) {
      return;
    }

    const nextAccountId = accountId || accountData?.AccountId || null;
    const settingsChanged = hydratedSettingsSignature !== currentSettingsSignature;

    if (hydratedAccountId !== nextAccountId || settingsChanged) {
      setHydratedAccountId(nextAccountId);
      setHydratedSettingsSignature(currentSettingsSignature);
      setSelectedUVarIds(initialSelectedUVarIds);
      setUVarHtmlAnsState(initialUVarHtmlAnsState);
      setEditingUVarMvarName(null);
      
      // Initialize PLA preloads/reports from PLA data
      const preloads: Record<string, AdminPreLoadDto[]> = {};
      const reports: Record<string, AdminAssessmentReportDto[]> = {};
      const returnUrls: Record<string, string> = {};

      plaList.forEach((pla) => {
        const key = `${pla.PlaId}:${pla.SCode}`;
        preloads[key] = pla.Preloads ?? [];
        reports[key] = pla.Reports ?? [];
        returnUrls[key] = pla.ReturnUrl ?? '';
      });

      setPlaPreloadsState(preloads);
      setPlaReportsState(reports);
      setPlaReturnUrlState(returnUrls);
      // enabledSegments/enabledReports/plaPreloadEditsState initialized by assessment loading effect
      setPlaPreloadEditsState({});
      setIsPlaEditDirty(false);
      
      hasPendingUserEditsRef.current = false;
      setSaveMessage(null);
    }
  }, [
    accountData,
    accountId,
    currentSettingsSignature,
    hasLoadedAccountSettings,
    hydratedAccountId,
    hydratedSettingsSignature,
    plaList,
    uVars,
    initialSelectedUVarIds,
    initialUVarHtmlAnsState,
  ]);
  
  // Load AdminAssessmentDto for each PLA from bundled local storage
  useEffect(() => {
    if (!adminAssessmentStorage || !hasLoadedAccountSettings) return;

    const loadAssessments = async () => {
      const loaded: Record<string, AdminAssessmentDto> = {};
      const enabledSegments: Record<string, Set<string>> = {};
      const enabledReports: Record<string, Set<string>> = {};

      const preloadEdits: Record<string, Record<string, string>> = {};

      for (const pla of plaList) {
        if (!pla.AssessmentId) continue;
        const assessment = await adminAssessmentStorage.getAssessmentBySourceId(pla.AssessmentId);
        if (assessment) {
          loaded[pla.AssessmentId] = assessment;
          const key = `${pla.PlaId}:${pla.SCode}`;

          // Initialize enabled segments from saved surveyblist preload; default to all if not yet saved.
          // Close (CLOSE_ADMIN_SEGMENT_ID) is force-included regardless of source — it's hidden
          // from the Measures UI and never optional, so a stored list that predates this rule
          // (or simply never had it) shouldn't leave it un-enabled.
          const savedSurveyblist = (pla.Preloads ?? []).find(p => p.FieldCode === 'surveyblist');
          if (savedSurveyblist) {
            const savedIds = savedSurveyblist.Value.split(',').map(s => s.trim()).filter(Boolean);
            enabledSegments[key] = new Set([...savedIds, CLOSE_ADMIN_SEGMENT_ID]);
          } else {
            const allSurveyIds = assessment.surveybList?.split(',').map(s => s.trim()).filter(Boolean) ?? [];
            enabledSegments[key] = new Set([...allSurveyIds, CLOSE_ADMIN_SEGMENT_ID]);
          }

          // Initialize enabled reports from saved pla.Reports; default to all if not yet saved.
          // Keyed by reportId (AdminAssessmentReportDto.reportId) — dName is just a display
          // label and isn't guaranteed unique/stable.
          if (pla.Reports != null) {
            const savedReportIds = pla.Reports.map(getReportId).filter((id): id is string => !!id);
            enabledReports[key] = new Set(savedReportIds);
          } else {
            const allReportIds = (assessment.reports ?? []).map(r => r.reportId).filter((id): id is string => !!id);
            enabledReports[key] = new Set(allReportIds);
          }

          // Initialize preload edit values from existing pla.Preloads, defaulting to skinAssessment
          // override values. Always seeded regardless of skinAssessment.PreloadOverrides — also
          // carries 'adminreportid' (the account-selected default report — see the Reports
          // dropdown below), which isn't a skin-defined override.
          const skinAssessment = skinAssessmentList.find(a => a.AssessmentId === pla.AssessmentId);
          const edits: Record<string, string> = {};
          (skinAssessment?.PreloadOverrides ?? []).forEach(override => {
            const existing = (pla.Preloads ?? []).find(p => p.FieldCode === override.FieldCode);
            edits[override.FieldCode] = existing?.Value ?? override.Value ?? '';
          });
          edits['adminreportid'] = (pla.Preloads ?? []).find(p => p.FieldCode === 'adminreportid')?.Value ?? '';
          // Same seeding for "Default Report" — this was missed when that dropdown was added,
          // so plaPreloadEditsState[key]['defaultreportid'] was always undefined on load and
          // the dropdown fell back to displaying enabledReportOptions[0] instead of whatever
          // had actually been saved.
          edits['defaultreportid'] = (pla.Preloads ?? []).find(p => p.FieldCode === 'defaultreportid')?.Value ?? '';
          preloadEdits[key] = edits;
        }
      }

      setCachedAssessments(loaded);
      setPlaEnabledSegments(enabledSegments);
      setPlaEnabledReports(enabledReports);
      setPlaPreloadEditsState(preloadEdits);
    };

    loadAssessments();
  }, [adminAssessmentStorage, hasLoadedAccountSettings, plaList]);
  
  // Load AdminSegments for all PLAs eagerly since data is bundled
  useEffect(() => {
    if (!adminSegmentStorage || !contentService || !hasLoadedAccountSettings || Object.keys(cachedAssessments).length === 0) {
      return;
    }

    const loadSegments = async () => {
      const segmentsToLoad: string[] = [];
      
      // Collect all unique surveybIds across all PLAs
      plaList.forEach((pla) => {
        const assessment = pla.AssessmentId ? cachedAssessments[pla.AssessmentId] : null;
        if (assessment?.surveybList) {
          const surveybIds = assessment.surveybList.split(',').map(s => s.trim()).filter(Boolean);
          surveybIds.forEach(id => {
            if (!segmentsToLoad.includes(id)) {
              segmentsToLoad.push(id);
            }
          });
        }
      });

      // Load all segments
      const loadedSegments: Record<string, AdminSegmentDto> = {};
      for (const surveybId of segmentsToLoad) {
        try {
          const segment = await adminSegmentStorage.getAdminSegment(surveybId, languageTypeId, contentService);
          if (segment) {
            loadedSegments[surveybId] = segment;
          }
        } catch (error) {
          console.warn(`Failed to load segment ${surveybId}:`, error);
        }
      }
      
      setSegmentCache(loadedSegments);
    };

    loadSegments();
  }, [adminSegmentStorage, contentService, hasLoadedAccountSettings, plaList, cachedAssessments, languageTypeId]);

  const markUserEdit = () => {
    hasPendingUserEditsRef.current = true;
    setIsPlaEditDirty(true);
    setSaveMessage(null);
  };

  const updatePreloadValue = (plaKey: string, fieldCode: string, value: string) => {
    setPlaPreloadEditsState(prev => ({
      ...prev,
      [plaKey]: { ...(prev[plaKey] ?? {}), [fieldCode]: value },
    }));
    markUserEdit();
  };

  const updateReturnUrl = (plaKey: string, value: string) => {
    setPlaReturnUrlState(prev => ({ ...prev, [plaKey]: value }));
    markUserEdit();
  };

  const updateUVarSelection = (selectedIds: string[]) => {
    setUVarHtmlAnsState((prev) => {
      const next: Record<string, AdminHtmlAnswerDto[]> = {};
      selectedIds.forEach((mvarName) => {
        next[mvarName] = prev[mvarName] ?? [];
      });
      return next;
    });
    setEditingUVarMvarName(null);
    setSelectedUVarIds(selectedIds);
    markUserEdit();
  };

  const editingUVarSkinEntry = useMemo(() => {
    if (!editingUVarMvarName) {
      return null;
    }
    return skinUVars.find((item) => item.MvarName === editingUVarMvarName) ?? null;
  }, [editingUVarMvarName, skinUVars]);

  const htmlAnsOptions = useMemo(() => {
    return (editingUVarSkinEntry?.HtmlUvarAns ?? []).map((ans) => ({
      id: ans.KeyValue,
      label: ans.AText || ans.KeyValue,
    }));
  }, [editingUVarSkinEntry]);

  const selectedHtmlAnsIds = useMemo(() => {
    if (!editingUVarMvarName) {
      return [];
    }
    return (uVarHtmlAnsState[editingUVarMvarName] ?? []).map((ans) => ans.KeyValue);
  }, [editingUVarMvarName, uVarHtmlAnsState]);

  const updateHtmlAnsSelection = (selectedIds: string[]) => {
    if (!editingUVarMvarName || !editingUVarSkinEntry) {
      return;
    }
    const sourceAns = editingUVarSkinEntry.HtmlUvarAns ?? [];
    const updatedAns = selectedIds
      .map((id) => sourceAns.find((ans) => ans.KeyValue === id))
      .filter((ans): ans is AdminHtmlAnswerDto => !!ans);
    setUVarHtmlAnsState((prev) => ({ ...prev, [editingUVarMvarName]: updatedAns }));
    markUserEdit();
  };

  const isDirty = useMemo(() => {
    const uVarsDirty = JSON.stringify(selectedUVarIds) !== JSON.stringify(initialSelectedUVarIds);

    const htmlAnsDirty = selectedUVarIds.some((mvarName) => {
      return JSON.stringify(uVarHtmlAnsState[mvarName] ?? []) !== JSON.stringify(initialUVarHtmlAnsState[mvarName] ?? []);
    });

    const plaPreloadsDirty = sortedPlaList.some((pla) => {
      const key = `${pla.PlaId}:${pla.SCode}`;
      return JSON.stringify(plaPreloadsState[key] ?? []) !== JSON.stringify(pla.Preloads ?? []);
    });
    
    const plaReportsDirty = sortedPlaList.some((pla) => {
      const key = `${pla.PlaId}:${pla.SCode}`;
      return JSON.stringify(plaReportsState[key] ?? []) !== JSON.stringify(pla.Reports ?? []);
    });
    
    return uVarsDirty || htmlAnsDirty || plaPreloadsDirty || plaReportsDirty || isPlaEditDirty;
  }, [selectedUVarIds, initialSelectedUVarIds, uVarHtmlAnsState, initialUVarHtmlAnsState, plaPreloadsState, plaReportsState, sortedPlaList, isPlaEditDirty]);

  const handleSave = async () => {
    if (isSavingRemote) {
      return;
    }

    const resolvedAccountId = (accountId || accountData?.AccountId || '').trim();
    if (!resolvedAccountId) {
      setSaveMessage('Could not save: missing account id.');
      return;
    }

    if (!resolvedApiService?.processServiceRequestWithRefresh) {
      setSaveMessage('Could not save: account settings service unavailable.');
      return;
    }

    const existingSettings = accountData?.AccountSettings;
    if (!existingSettings) {
      setSaveMessage('Could not save: account settings are not loaded yet.');
      return;
    }

    // Build UVars list from selected IDs (including per-uvar HtmlUvarAns) — computed before
    // updatedPlaList below so every saved PLA can carry a copy (see comment there for why).
    const updatedUVars: AdminUVarDto[] = selectedUVarIds
      .map((id) => {
        const skinUVar = skinUVars.find((item) => item.MvarName === id);
        if (!skinUVar) {
          return null;
        }
        return {
          ...skinUVar,
          HtmlUvarAns: uVarHtmlAnsState[id] ?? [],
        };
      })
      .filter((item): item is AdminUVarDto => !!item);

    // Update PlaList: merge preload edits + surveyblist measure selection; replace reports with enabled set
    const updatedPlaList = plaList.map((pla) => {
      const key = `${pla.PlaId}:${pla.SCode}`;
      const assessment = pla.AssessmentId ? cachedAssessments[pla.AssessmentId] : null;
      const skinAssessment = pla.AssessmentId
        ? skinAssessmentList.find(a => a.AssessmentId === pla.AssessmentId)
        : null;

      // Start with existing preloads (preserves non-form preloads)
      const updatedPreloads: AdminPreLoadDto[] = [...(pla.Preloads ?? [])];

      // Merge in user-edited preload values by FieldCode
      const edits = plaPreloadEditsState[key] ?? {};
      (skinAssessment?.PreloadOverrides ?? []).forEach(override => {
        const editedValue = edits[override.FieldCode];
        if (editedValue !== undefined) {
          const idx = updatedPreloads.findIndex(p => p.FieldCode === override.FieldCode);
          const updated: AdminPreLoadDto = {
            FieldCode: override.FieldCode,
            Type: override.Type,
            Value: editedValue,
            LanguageTypeId: override.LanguageTypeId || '1',
          };
          if (idx >= 0) { updatedPreloads[idx] = updated; } else { updatedPreloads.push(updated); }
        }
      });

      // Build/update the surveyblist preload from enabled measures (in original template order).
      // Close (CLOSE_ADMIN_SEGMENT_ID) is force-included here too, defensively — it's never
      // shown as a toggleable checkbox so plaEnabledSegments should already carry it from
      // initialization, but this keeps a PLA saved before that fix from silently dropping it.
      // Same for the sole remaining (non-Close) measure when there's only one — its checkbox
      // is disabled-and-checked in the UI (see isSoleMeasure above), so force it in here too
      // rather than trust whatever plaEnabledSegments happened to hold.
      if (assessment?.surveybList) {
        const allSurveyIds = assessment.surveybList.split(',').map(s => s.trim()).filter(Boolean);
        const nonCloseSurveyIds = allSurveyIds.filter(id => id !== CLOSE_ADMIN_SEGMENT_ID);
        const soleMeasureId = nonCloseSurveyIds.length === 1 ? nonCloseSurveyIds[0] : null;
        const enabledSet = new Set([
          ...(plaEnabledSegments[key] ?? new Set<string>()),
          CLOSE_ADMIN_SEGMENT_ID,
          ...(soleMeasureId ? [soleMeasureId] : []),
        ]);
        const surveyblistPreload: AdminPreLoadDto = {
          FieldCode: 'surveyblist',
          Type: 'string',
          Value: allSurveyIds.filter(id => enabledSet.has(id)).join(','),
          LanguageTypeId: '1',
        };
        const surveyIdx = updatedPreloads.findIndex(p => p.FieldCode === 'surveyblist');
        if (surveyIdx >= 0) { updatedPreloads[surveyIdx] = surveyblistPreload; } else { updatedPreloads.push(surveyblistPreload); }
      }

      // Build/update the adminreportid preload from the account's selected default report
      // (the "Admin Report" dropdown below the Reports checkboxes) — readable via
      // getPreload('adminreportid') from PreScript, e.g. renderReport(getPreload('adminreportid')).
      const adminReportIdValue = plaPreloadEditsState[key]?.['adminreportid'] ?? '';
      const adminReportIdPreload: AdminPreLoadDto = {
        FieldCode: 'adminreportid',
        Type: 'string',
        Value: adminReportIdValue,
        LanguageTypeId: '1',
      };
      const adminReportIdx = updatedPreloads.findIndex(p => p.FieldCode === 'adminreportid');
      if (adminReportIdx >= 0) { updatedPreloads[adminReportIdx] = adminReportIdPreload; } else { updatedPreloads.push(adminReportIdPreload); }

      // Build/update the defaultreportid preload — the Client tab's default report for the
      // profile header row's View/PDF icons and the assessment history rows' Report-selection
      // dropdown (see resolveDefaultReportId in ClientReportAssessments.ts). No None option:
      // falls back to the first enabled report (in template order) when nothing's explicitly
      // chosen, so this is never persisted blank as long as at least one report is enabled.
      const enabledReportIdsForDefault = plaEnabledReports[key] ?? new Set<string>();
      const firstEnabledReportId = (assessment?.reports ?? [])
        .map(getReportId)
        .find(id => id && enabledReportIdsForDefault.has(id)) ?? '';
      const defaultReportIdValue = plaPreloadEditsState[key]?.['defaultreportid'] || firstEnabledReportId;
      const defaultReportIdPreload: AdminPreLoadDto = {
        FieldCode: 'defaultreportid',
        Type: 'string',
        Value: defaultReportIdValue,
        LanguageTypeId: '1',
      };
      const defaultReportIdx = updatedPreloads.findIndex(p => p.FieldCode === 'defaultreportid');
      if (defaultReportIdx >= 0) { updatedPreloads[defaultReportIdx] = defaultReportIdPreload; } else { updatedPreloads.push(defaultReportIdPreload); }

      // Replace reports with the enabled subset in template order, keyed by reportId
      // (dName is just a display label). `assessment.reports` (content/template model,
      // types/adminAssessment.ts) and `pla.Reports` (account settings API model,
      // types/api/service.ts) are two independently-defined DTOs with the same name but
      // different nested casing — this cast reflects a pre-existing cross-model seam, not a
      // behavior change.
      const enabledReportSet = plaEnabledReports[key] ?? new Set<string>();
      const updatedReports = (assessment?.reports ?? pla.Reports ?? [])
        .filter(r => enabledReportSet.has(getReportId(r)));

      // "Return Website" — the post-completion redirect target for this PLA. The backend loads
      // SecureLoginResponseDto.completeUrl from here for /link sessions; a blank value there is
      // fine (backend falls back to the account-level Return Website), so persist exactly what
      // the provider typed rather than materializing the account default.
      const returnUrl = (plaReturnUrlState[key] ?? pla.ReturnUrl ?? '').trim();

      // The Customize section's uvars are account-wide (no per-PLA editor — see
      // AssessmentOptionsView's "general" section), but AdminStateStorage.resolveAccountUVars
      // and ClientLaunchBuilder.ts both read UVars off the *PLA* record (matching how Preloads
      // already works), not off AccountSettingsDto.UVars directly — /link and /remote
      // administrations resolve their uvar config from AccountPlaDto.UVars, and /secure
      // launches (ClientLaunchBuilder) copy it into LaunchSecureDto.UVars. So every saved PLA
      // gets the same current uvars catalog; a question only ever picks up the entries whose
      // UvarTypeId it actually references (see resolveUvarAnswers in AdminView.tsx).
      return {
        ...pla,
        Preloads: updatedPreloads,
        Reports: updatedReports as unknown as AdminAssessmentReportDto[],
        ReturnUrl: returnUrl,
        UVars: updatedUVars,
      };
    });

    const nextAccountSettingsUpdate: AccountSettingsUpdateDto = {
      PracInfo: existingSettings.PracInfo,
      Enables: existingSettings.Enables ?? [],
      UVars: updatedUVars,
      PracPhi: existingSettings.PracPhi ?? [],
      Registries: existingSettings.Registries ?? [],
      XInfo: existingSettings.XInfo ?? [],
      PlaList: updatedPlaList,
    };

    const payload: PracAccountSettingUpdateDto = {
      AccountId: resolvedAccountId,
      AccountSettingsUpdate: nextAccountSettingsUpdate,
      Status: '1',
    };

    try {
      setIsSavingRemote(true);
      setSaveMessage(null);

      const response = await resolvedApiService.processServiceRequestWithRefresh({
        RequestType: 'provider_accountsetting_put',
        Payload: JSON.stringify(payload),
      }, 'sync', {
        userStore: resolveUserStore(),
        storage,
      });

      let responseDto: PracAccountSettingUpdateDto | null = null;
      if (response?.payload) {
        try {
          responseDto = JSON.parse(response.payload) as PracAccountSettingUpdateDto;
        } catch {
          responseDto = null;
        }
      }

      const persistedSettingsUpdate = responseDto?.AccountSettingsUpdate ?? nextAccountSettingsUpdate;
      const persistedSettings: AccountSettingsDto = {
        ...existingSettings,
        ...persistedSettingsUpdate,
      };

      if (storage) {
        try {
          const pracStorage = new AccountPracInfoStorage(storage);
          const cached = await pracStorage.getAccountPracInfo(resolvedAccountId);

          if (cached) {
            await pracStorage.saveAccountPracInfo(resolvedAccountId, {
              ...cached,
              AccountSettings: persistedSettings,
            });
          }
        } catch (saveError) {
          console.warn('AssessmentOptionsView: failed to persist account settings cache after provider_accountsetting_put', saveError);
        }
      }

      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('progroups:sra-config-updated', {
          detail: {
            accountId: resolvedAccountId,
          },
        }));
      }

      setSaveMessage('Assessment options saved.');
      hasPendingUserEditsRef.current = false;
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : String(saveError);
      setSaveMessage(`Save failed: ${message}`);
      console.warn('AssessmentOptionsView: provider_accountsetting_put failed', saveError);
    } finally {
      setIsSavingRemote(false);
    }
  };

  const renderGeneralSection = () => {
    const isWeb = Platform.OS === 'web' || typeof document !== 'undefined';
    return (
      <View style={styles.sectionContent}>
        {selectedUVarIds.length > 0 ? (
          <View style={styles.uVarTable}>
            {selectedUVarIds.map((mvarName) => {
              const skinEntry = skinUVars.find((s) => s.MvarName === mvarName);
              const selectedAns = uVarHtmlAnsState[mvarName] ?? [];
              const isEditingThis = editingUVarMvarName === mvarName;
              return (
                <View key={mvarName} style={styles.uVarTableRow}>
                  <View style={styles.uVarTableDNameCell}>
                    <Text style={styles.uVarDNameText}>{skinEntry?.DName || mvarName}</Text>
                  </View>
                  <View style={styles.uVarTableDropdownCell}>
                    {isWeb ? (
                      <select
                        style={{ ...webControlStyle, width: '100%', borderRadius: '4px', padding: '2px 4px' } as any}
                        size={1}
                      >
                        {selectedAns.length === 0
                          ? <option value="">— none selected —</option>
                          : selectedAns.map((ans) => (
                              <option key={ans.KeyValue} value={ans.KeyValue}>
                                {ans.AText || ans.KeyValue}
                              </option>
                            ))}
                      </select>
                    ) : (
                      <Text style={styles.infoText}>{selectedAns.length} answer(s)</Text>
                    )}
                  </View>
                  <View style={styles.uVarTableEditCell}>
                    <Pressable
                      style={[styles.settingEditBtn, isEditingThis && styles.settingEditBtnActive]}
                      onPress={() => {
                        setIsEditingUVars(false);
                        editingUVarsRef.current = false;
                        setEditingUVarMvarName(isEditingThis ? null : mvarName);
                      }}
                    >
                      <Text style={styles.settingEditBtnText}>Edit</Text>
                    </Pressable>
                  </View>
                </View>
              );
            })}
          </View>
        ) : null}
        <View style={styles.sectionRow}>
          <Text style={styles.infoText}></Text>
          <Pressable
            style={[styles.manageButton, isEditingUVars && styles.manageButtonActive]}
            onPress={() => {
              const nextState = !isEditingUVars;
              editingUVarsRef.current = nextState;
              setIsEditingUVars(nextState);
              setEditingUVarMvarName(null);
            }}
          >
            <Text style={styles.manageButtonText}>Manage List</Text>
          </Pressable>
        </View>
      </View>
    );
  };

  const renderPlaSection = (pla: AccountPlaDto) => {
    const key = `${pla.PlaId}:${pla.SCode}`;
    const preloads = plaPreloadsState[key] ?? [];
    const reports = plaReportsState[key] ?? [];
    const enabledSegments = plaEnabledSegments[key] ?? new Set();
    const enabledReports = plaEnabledReports[key] ?? new Set();
    
    // Find the assessment definition from AdminAssessmentStorage cache
    const assessment = pla.AssessmentId ? cachedAssessments[pla.AssessmentId] : null;
    
    if (!pla.AssessmentId) {
      return (
        <View style={styles.sectionContent}>
          <Text style={styles.infoText}>No assessment assigned</Text>
        </View>
      );
    }
    
    if (!assessment) {
      return (
        <View style={styles.sectionContent}>
          <Text style={styles.infoText}>Loading assessment data...</Text>
        </View>
      );
    }
    
    // Parse surveybList (comma-separated segment IDs) — Close is never shown here; it's always
    // enabled (see CLOSE_ADMIN_SEGMENT_ID and its force-inclusion at load/save time above).
    const surveybIds = (assessment.surveybList?.split(',').map(s => s.trim()).filter(Boolean) ?? [])
      .filter(id => id !== CLOSE_ADMIN_SEGMENT_ID);
    
    const toggleSegment = (surveybId: string) => {
      setPlaEnabledSegments(prev => {
        const updated = new Set(prev[key] ?? new Set());
        if (updated.has(surveybId)) {
          updated.delete(surveybId);
        } else {
          updated.add(surveybId);
        }
        return { ...prev, [key]: updated };
      });
      markUserEdit();
    };
    
    const toggleReport = (reportId: string) => {
      setPlaEnabledReports(prev => {
        const updated = new Set(prev[key] ?? new Set());
        const wasEnabled = updated.has(reportId);
        if (wasEnabled) {
          updated.delete(reportId);
        } else {
          updated.add(reportId);
        }
        return { ...prev, [key]: updated };
      });
      // A report that's no longer enabled can't stay selected as the account's default
      // "Admin Report" — clear it rather than leave a dangling selection.
      if ((plaPreloadEditsState[key] ?? {})['adminreportid'] === reportId) {
        updatePreloadValue(key, 'adminreportid', '');
      }
      // Same for "Default Report" — clearing here just means the UI/save-time fallback picks
      // the next remaining enabled report instead (see the Default Report dropdown below and
      // its handleSave fallback); "Default Report" itself has no blank/None state once at
      // least one report is enabled.
      if ((plaPreloadEditsState[key] ?? {})['defaultreportid'] === reportId) {
        updatePreloadValue(key, 'defaultreportid', '');
      }
      markUserEdit();
    };

    // Structural param, not AdminAssessmentReportDto — callers pass entries from
    // `assessment.reports` (content model, types/adminAssessment.ts) whose nested
    // `reportPreloads` element type differs from the account-settings API model's; only
    // dName/targetType are read here and those match across both.
    const reportLabel = (report: { dName: string; targetType?: string }): string =>
      report.targetType ? `${report.dName} (${report.targetType})` : report.dName;

    const skinAssessment = pla.AssessmentId
      ? skinAssessmentList.find(a => a.AssessmentId === pla.AssessmentId)
      : null;
    const preloadOverrides = skinAssessment?.PreloadOverrides ?? [];

    return (
      <View style={styles.sectionContent}>
        {/* Preloads - full width */}
        <View style={styles.plaSubsection}>
         {/* 2-column row: Measures | Reports */}
        <View style={styles.plaSubsectionRow}>
          {/* Measures (left) */}
          <View style={styles.plaSubsectionHalf}>
            <Text style={styles.subsectionLabel}>Measures</Text>
            {surveybIds.length === 0 ? (
              <Text style={styles.infoText}>No measures defined</Text>
            ) : (
              surveybIds.map((surveybId) => {
                // With only one measure left (Close already excluded from this list), it's the
                // only option — force it checked and non-interactive rather than let it be
                // unchecked down to zero measures.
                const isSoleMeasure = surveybIds.length === 1;
                const isEnabled = isSoleMeasure ? true : enabledSegments.has(surveybId);
                const segment = segmentCache[surveybId];
                const displayName = segment?.Title || surveybId;
                if (Platform.OS === 'web') {
                  return (
                    <View key={surveybId} style={styles.checkboxRow}>
                      {React.createElement('input' as any, {
                        type: 'checkbox',
                        checked: isEnabled,
                        disabled: isSoleMeasure,
                        onChange: () => toggleSegment(surveybId),
                        'aria-label': displayName,
                        style: {
                          width: '18px', height: '18px', accentColor: EDITOR_COLORS.accent,
                          cursor: isSoleMeasure ? 'default' : 'pointer',
                        } as any,
                      })}
                      <Text style={styles.checkboxLabel}>{displayName}</Text>
                    </View>
                  );
                }
                return (
                  <Pressable
                    key={surveybId}
                    style={styles.checkboxRow}
                    onPress={() => { if (!isSoleMeasure) toggleSegment(surveybId); }}
                    disabled={isSoleMeasure}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: isEnabled, disabled: isSoleMeasure }}
                  >
                    <View style={[styles.checkbox, isEnabled && styles.checkboxChecked]}>
                      {isEnabled && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <Text style={styles.checkboxLabel}>{displayName}</Text>
                  </Pressable>
                );
              })
            )}
          </View>

          {/* Reports (right) */}
          <View style={styles.plaSubsectionHalfWide}>
            <Text style={styles.subsectionLabel}>Reports</Text>
            {!assessment.reports || assessment.reports.length === 0 ? (
              <Text style={styles.infoText}>No reports defined</Text>
            ) : (
              assessment.reports.map((report) => {
                const isEnabled = enabledReports.has(report.reportId);
                if (Platform.OS === 'web') {
                  return (
                    <View key={report.reportId} style={styles.checkboxRow}>
                      {React.createElement('input' as any, {
                        type: 'checkbox',
                        checked: isEnabled,
                        onChange: () => toggleReport(report.reportId),
                        'aria-label': reportLabel(report),
                        style: { width: '18px', height: '18px', accentColor: EDITOR_COLORS.accent, cursor: 'pointer' } as any,
                      })}
                      <Text style={styles.checkboxLabel}>{reportLabel(report)}</Text>
                    </View>
                  );
                }
                return (
                  <Pressable
                    key={report.reportId}
                    style={styles.checkboxRow}
                    onPress={() => toggleReport(report.reportId)}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: isEnabled }}
                  >
                    <View style={[styles.checkbox, isEnabled && styles.checkboxChecked]}>
                      {isEnabled && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <Text style={styles.checkboxLabel}>{reportLabel(report)}</Text>
                  </Pressable>
                );
              })
            )}
            {(() => {
              const enabledReportOptions = (assessment.reports ?? []).filter(r => enabledReports.has(r.reportId));
              const selectedAdminReportId = (plaPreloadEditsState[key] ?? {})['adminreportid'] ?? '';
              const selectedAdminReportLabel = enabledReportOptions.find(r => r.reportId === selectedAdminReportId);

              // "Default Report" — which report the Client tab uses for the View/PDF icons in
              // the profile header row and as the initial Report-selection dropdown value in
              // the assessment history rows (see resolveDefaultReportId in
              // ClientReportAssessments.ts). Unlike "Admin Report" above (which drives
              // renderReport(...) at the END of an administration and can legitimately be
              // "None"), Default Report always needs a real value once any report is enabled —
              // no None option — so an unset/cleared selection displays (and, on save, is
              // persisted as) the first enabled report rather than a blank choice.
              const selectedDefaultReportId = (plaPreloadEditsState[key] ?? {})['defaultreportid'] || enabledReportOptions[0]?.reportId || '';
              const selectedDefaultReportLabel = enabledReportOptions.find(r => r.reportId === selectedDefaultReportId);

              return (
                <>
                  <View style={styles.adminReportRow}>
                    <Text style={styles.adminReportLabel}>Admin Report</Text>
                    {Platform.OS === 'web' ? (
                      <select
                        style={{ ...webControlStyle, width: '100%' } as any}
                        value={selectedAdminReportId}
                        onChange={(event: any) => updatePreloadValue(key, 'adminreportid', String(event?.target?.value ?? ''))}
                      >
                        <option value="">None</option>
                        {enabledReportOptions.map((report) => (
                          <option key={report.reportId} value={report.reportId}>
                            {reportLabel(report)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Pressable
                        style={styles.dropdownInlineBtn}
                        onPress={() => {
                          const options = ['', ...enabledReportOptions.map(r => r.reportId)];
                          const currentIdx = options.indexOf(selectedAdminReportId);
                          const nextIdx = currentIdx < 0 || currentIdx === options.length - 1 ? 0 : currentIdx + 1;
                          updatePreloadValue(key, 'adminreportid', options[nextIdx]);
                        }}
                      >
                        <Text style={styles.dropdownInlineText} numberOfLines={1}>
                          {selectedAdminReportLabel ? reportLabel(selectedAdminReportLabel) : 'None'}
                        </Text>
                        <Text style={styles.dropdownInlineChevron}>▼</Text>
                      </Pressable>
                    )}
                  </View>
                  <View style={styles.adminReportRow}>
                    <Text style={styles.adminReportLabel}>Default Report</Text>
                    {enabledReportOptions.length === 0 ? (
                      <Text style={styles.infoText}>Enable a report above first</Text>
                    ) : Platform.OS === 'web' ? (
                      <select
                        style={{ ...webControlStyle, width: '100%' } as any}
                        value={selectedDefaultReportId}
                        onChange={(event: any) => updatePreloadValue(key, 'defaultreportid', String(event?.target?.value ?? ''))}
                      >
                        {enabledReportOptions.map((report) => (
                          <option key={report.reportId} value={report.reportId}>
                            {reportLabel(report)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Pressable
                        style={styles.dropdownInlineBtn}
                        onPress={() => {
                          const options = enabledReportOptions.map(r => r.reportId);
                          const currentIdx = options.indexOf(selectedDefaultReportId);
                          const nextIdx = currentIdx < 0 || currentIdx === options.length - 1 ? 0 : currentIdx + 1;
                          updatePreloadValue(key, 'defaultreportid', options[nextIdx]);
                        }}
                      >
                        <Text style={styles.dropdownInlineText} numberOfLines={1}>
                          {selectedDefaultReportLabel ? reportLabel(selectedDefaultReportLabel) : ''}
                        </Text>
                        <Text style={styles.dropdownInlineChevron}>▼</Text>
                      </Pressable>
                    )}
                  </View>
                </>
              );
            })()}
          </View>
        </View>
         <Text style={styles.subsectionLabel}>Preloads</Text>
          {preloadOverrides.length === 0 ? (
            <Text style={styles.infoText}>No preloads defined</Text>
          ) : (
            preloadOverrides.map((override, idx) => {
              const currentValue = (plaPreloadEditsState[key] ?? {})[override.FieldCode] ?? '';
              const isBool = override.Type === 'bool';
              const isChecked = currentValue === '1';
              return (
                <View key={idx} style={styles.preloadRow}>
                  <Text style={styles.preloadLabel}>{override.DName}</Text>
                  {isBool ? (
                    Platform.OS === 'web' ? (
                      React.createElement('input' as any, {
                        type: 'checkbox',
                        checked: isChecked,
                        onChange: () => updatePreloadValue(key, override.FieldCode, isChecked ? '0' : '1'),
                        'aria-label': override.DName,
                        style: { width: '18px', height: '18px', accentColor: EDITOR_COLORS.accent, cursor: 'pointer' } as any,
                      })
                    ) : (
                      <Pressable
                        onPress={() => updatePreloadValue(key, override.FieldCode, isChecked ? '0' : '1')}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: isChecked }}
                      >
                        <View style={[styles.checkbox, isChecked && styles.checkboxChecked]}>
                          {isChecked && <Text style={styles.checkmark}>✓</Text>}
                        </View>
                      </Pressable>
                    )
                  ) : (
                    <TextInput
                      value={currentValue}
                      onChangeText={(text) => updatePreloadValue(key, override.FieldCode, text)}
                      style={styles.preloadInput}
                    />
                  )}
                </View>
              );
            })
          )}
        </View>

        {/* Return Website — post-completion redirect target for this PLA (AccountPlaDto.ReturnUrl).
            The backend populates SecureLoginResponseDto.completeUrl from this for /link sessions;
            left blank, it falls back to the account-level Return Website (shown as the placeholder). */}
        <View style={styles.plaSubsection}>
          <Text style={styles.subsectionLabel}>Return Website</Text>
          <View style={styles.preloadRow}>
            <Text style={styles.preloadLabel}>Redirect on completion</Text>
            <TextInput
              value={plaReturnUrlState[key] ?? ''}
              onChangeText={(text) => updateReturnUrl(key, text)}
              placeholder={accountReturnWebsite || 'Account Return Website'}
              placeholderTextColor={EDITOR_COLORS.placeholder}
              autoCapitalize="none"
              keyboardType="url"
              style={styles.preloadInput}
            />
          </View>
        </View>

      </View>
    );
  };

  const sections = useMemo(() => {
    const result: Array<{ key: string; label: string }> = [
      { key: 'general', label: 'Customize' },
    ];

    sortedPlaList.forEach((pla) => {
      result.push({
        key: `${pla.PlaId}:${pla.SCode}`,
        label: pla.DName || pla.SCode || 'Website Link',
      });
    });

    return result;
  }, [sortedPlaList]);

  if (error) {
    return (
      <View style={styles.panel}>
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>Error</Text>
          <Text style={styles.errorBody}>{error}</Text>
        </View>
      </View>
    );
  }

  if (isLoading || !localStateMatchesCurrentSettings) {
    return (
      <View style={styles.panel}>
        <View style={styles.loadingRow}>
          <Text style={styles.loadingText}>Loading assessment options...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.panel}>
      <View style={styles.topActionsRow}>
        <Pressable
          style={[styles.saveButton, (!isDirty || isSavingRemote) && styles.saveButtonDisabled]}
          onPress={handleSave}
          disabled={!isDirty || isSavingRemote}
        >
          <Text style={[styles.saveButtonText, (!isDirty || isSavingRemote) && styles.saveButtonTextDisabled]}>
            {isSavingRemote ? 'Saving...' : 'Save'}
          </Text>
        </Pressable>
      </View>

      {saveMessage ? (
        <View style={styles.saveMessageRow}>
          <Text style={styles.saveMessageText}>{saveMessage}</Text>
        </View>
      ) : null}

      {isEditingUVars ? (
        <View style={styles.settingEditPanel}>
          <View style={styles.settingEditFormRow}>
            <OrderedDualListSelector
              options={uVarOptions}
              selectedIds={selectedUVarIds}
              onChange={updateUVarSelection}
              availableTitle="Available UVars"
              selectedTitle="Selected UVars"
            />
          </View>
        </View>
      ) : null}

      {editingUVarMvarName ? (
        <View style={styles.settingEditPanel}>
          <Text style={styles.editingUVarTitle}>
            Answers: {skinUVars.find((s) => s.MvarName === editingUVarMvarName)?.DName || editingUVarMvarName}
          </Text>
          <View style={styles.settingEditFormRow}>
            <OrderedDualListSelector
              options={htmlAnsOptions}
              selectedIds={selectedHtmlAnsIds}
              onChange={updateHtmlAnsSelection}
              availableTitle="Available Answers"
              selectedTitle="Selected Answers"
            />
          </View>
        </View>
      ) : null}

      <View style={styles.editPanel}>
        <View style={styles.editTable}>
          {sections.map((section, index) => (
            <React.Fragment key={section.key}>
              <View style={styles.editTableRow}>
                <View style={styles.editTableLabelCell}>
                  <Text style={styles.editTableLabel}>{section.label}</Text>
                </View>
                <View style={styles.editTableContentCell}>
                  {section.key === 'general'
                    ? renderGeneralSection()
                    : renderPlaSection(sortedPlaList.find((pla) => `${pla.PlaId}:${pla.SCode}` === section.key)!)}
                </View>
              </View>

              {index < sections.length - 1 ? (
                <View style={styles.editTableSpacerRow}>
                  <View style={styles.editTableSpacerLabelCell} />
                  <View style={styles.editTableSpacerContentCell} />
                </View>
              ) : null}
            </React.Fragment>
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
    paddingTop: ASSESSMENT_OPTIONS_SPACING.topPanelPadding,
    backgroundColor: EDITOR_COLORS.background,
  },
  topActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingBottom: ASSESSMENT_OPTIONS_SPACING.topActionsBottom,
  },
  saveButton: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    backgroundColor: EDITOR_COLORS.accent,
    borderRadius: 6,
  },
  saveButtonDisabled: {
    backgroundColor: '#e0e7ff',
  },
  saveButtonText: {
    ...editorText,
    color: EDITOR_COLORS.background,
  },
  saveButtonTextDisabled: {
    color: EDITOR_COLORS.placeholder,
  },
  saveMessageRow: {
    paddingBottom: 12,
  },
  saveMessageText: {
    ...editorText,
    color: EDITOR_COLORS.successText,
  },
  errorCard: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  errorTitle: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    marginBottom: 4,
  },
  errorBody: {
    ...editorText,
    lineHeight: 20,
    color: EDITOR_COLORS.errorText,
  },
  loadingRow: {
    paddingBottom: 12,
  },
  loadingText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  editPanel: {
    paddingTop: ASSESSMENT_OPTIONS_SPACING.saveButtonTop,
  },
  editTable: {
    flexDirection: 'column',
  },
  editTableRow: {
    width: '100%',
    flexDirection: 'row',
    minHeight: 30,
  },
  editTableLabelCell: {
    width: 150,
    borderRightWidth: 1,
    borderRightColor: EDITOR_COLORS.separator,
    paddingLeft: 8,
    paddingRight: 18,
    paddingTop: 13,
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  editTableLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    lineHeight: 20,
    textAlign: 'right',
  },
  editTableContentCell: {
    flex: 1,
    paddingLeft: 18,
    paddingRight: 12,
    paddingTop: 10,
    paddingBottom: 6,
    justifyContent: 'flex-start',
  },
  editTableSpacerRow: {
    width: '100%',
    flexDirection: 'row',
    minHeight: ASSESSMENT_OPTIONS_SPACING.sectionSpacerRow,
  },
  editTableSpacerLabelCell: {
    width: 150,
  },
  editTableSpacerContentCell: {
    flex: 1,
  },
  sectionContent: {
    flexDirection: 'column',
    gap: 4,
  },
  sectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  manageButton: {
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 6,
    paddingBottom: 6,
    backgroundColor: EDITOR_COLORS.submitBackground,
    borderRadius: 4,
  },
  manageButtonActive: {
    backgroundColor: '#dbeafe',
    borderColor: '#3b82f6',
  },
  manageButtonText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  settingEditPanel: {
    paddingTop: 20,
    paddingBottom: 20,
  },
  settingEditFormRow: {
    paddingBottom: 12,
  },
  editingUVarTitle: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    paddingBottom: 8,
  },
  settingEditBtn: {
    width: 50,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  settingEditBtnActive: {
    borderColor: '#93c5fd',
    backgroundColor: '#eff6ff',
  },
  settingEditBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  uVarTable: {
    flexDirection: 'column',
    gap: 4,
    paddingBottom: 8,
  },
  uVarTableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  uVarTableDNameCell: {
    width: 140,
  },
  uVarTableDropdownCell: {
    flex: 1,
  },
  uVarTableEditCell: {
    width: 54,
    alignItems: 'flex-end',
  },
  uVarDNameText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  placeholderText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    fontStyle: 'italic',
  },
  infoText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  plaSubsection: {
    marginTop: 0,
    marginBottom: 6,
  },
  plaSubsectionRow: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 4,
    marginBottom: 6,
  },
  plaSubsectionHalf: {
    flex: 1,
  },
  // Reports gets a larger share than Measures — report labels (dName + "(targetType)") run
  // longer and were wrapping onto multiple lines in an equal-width column.
  plaSubsectionHalfWide: {
    flex: 1.6,
  },
  subsectionLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    marginBottom: 4,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 3,
    paddingLeft: 4,
    gap: 8,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.background,
  },
  checkboxChecked: {
    backgroundColor: EDITOR_COLORS.accent,
    borderColor: EDITOR_COLORS.accent,
  },
  checkmark: {
    fontSize: 12,
    color: EDITOR_COLORS.background,
    fontWeight: '600',
  },
  checkboxLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  overrideRow: {
    paddingVertical: 4,
    paddingLeft: 4,
  },
  overrideText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  overrideSubText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    fontStyle: 'italic',
  },
  preloadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 3,
    gap: 8,
  },
  preloadLabel: {
    ...editorText,
    // Widened from 130 — the field code's display name (override.DName) is often a long,
    // space-sparse technical string (e.g. a concatenated identifier) that wrapped to a second
    // line at the old width, making the Preloads list harder to scan.
    width: 220,
    flexShrink: 0,
    color: EDITOR_COLORS.textSecondary,
  },
  preloadInput: {
    ...editorText,
    flex: 1,
    color: EDITOR_COLORS.textPrimary,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    backgroundColor: EDITOR_COLORS.background,
  },
  adminReportRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 3,
    marginTop: 8,
    gap: 8,
  },
  // Unlike preloadLabel (fixed width, shared with the longer field-override labels below),
  // this hugs its own content ("Admin Report" is short) so the dropdown next to it — whose
  // own option text can run long (dName + "(targetType)") — gets as much room as possible.
  adminReportLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    flexShrink: 0,
  },
  dropdownInlineBtn: {
    flex: 1,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: EDITOR_COLORS.background,
  },
  dropdownInlineText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  dropdownInlineChevron: {
    fontFamily: 'Arial',
    fontSize: 11,
    color: EDITOR_COLORS.textMuted,
  },
});
